import React from 'react';
import type { RoastStageState } from './types';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { RoastStageDataSection } from './RoastStageDataSection';
import { RoastTimePhasesSection } from './RoastTimePhasesSection';
import { RoastWeightAnalysisSection } from './RoastWeightAnalysisSection';
import { RoastAgtronSection } from './RoastAgtronSection';
import { getAgtronClassification } from './RoastProfileUtils';

interface Props {
  stages: RoastStageState[];
  onStageChange: (index: number, field: keyof RoastStageState, value: string | number) => void;
  weightInput: number | undefined;
  calculatedWeight: number;
  onWeightInputChange: (value: number | undefined) => void;
  weightOutput: number | undefined;
  onWeightOutputChange: (value: number | undefined) => void;
  agtronWhole: string;
  agtronGround: string;
  onAgtronChange: (field: 'whole' | 'ground', value: string) => void;
  roastType: string;
  onRoastTypeChange: (value: string) => void;
  mode?: 'roast' | 'recipe';
  readOnly?: boolean;
  readOnlyStages?: boolean;
  readOnlyWeights?: boolean;
  readOnlyAgtron?: boolean;
  readOnlyRoastType?: boolean;
  allowRoastTypeEdit?: boolean;
  compactLayout?: boolean;
  disableWeightInput?: boolean;
  unit?: WeightUnit;
  onToggle?: (unit: WeightUnit) => void;
  showWorkOrderHints?: boolean;
}

const RoastResultSectionImpl: React.FC<Props> = ({
  stages,
  onStageChange,
  weightInput,
  calculatedWeight,
  onWeightInputChange,
  weightOutput,
  onWeightOutputChange,
  agtronWhole,
  agtronGround,
  onAgtronChange,
  roastType,
  onRoastTypeChange,
  mode = 'roast',
  readOnly = false,
  readOnlyStages,
  readOnlyWeights,
  readOnlyAgtron,
  readOnlyRoastType = false,
  allowRoastTypeEdit = false,
  compactLayout = false,
  disableWeightInput = false,
  unit = 'kg',
  onToggle,
  showWorkOrderHints = false,
}) => {
  const showTargetLabels = readOnly && mode === 'recipe';
  // --- Helpers ---
  const calculateShrinkage = () => {
    if (weightInput === undefined || weightOutput === undefined) return { percent: 0, weight: 0 };
    const out = Number(weightOutput);
    const diff = weightInput - out;
    const percent = (diff / weightInput) * 100;
    return {
      percent: percent.toFixed(1),
      weight: diff.toFixed(3),
    };
  };

  const timeToSeconds = (timeStr: string) => {
    const [mmRaw, ssRaw] = timeStr.split(':');
    const mm = Number((mmRaw ?? '').replace(/\D/g, ''));
    const ss = Number((ssRaw ?? '').replace(/\D/g, ''));
    return (Number.isFinite(mm) ? mm : 0) * 60 + (Number.isFinite(ss) ? ss : 0);
  };

  const secondsToTime = (totalSeconds: number) => {
    if (totalSeconds < 0) return '00:00';
    const m = Math.floor(totalSeconds / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const calculateDuration = (startKey: string, endKey: string) => {
    const startStage = stages.find(s => s.key === startKey);
    const endStage = stages.find(s => s.key === endKey);
    
    if (!startStage || !endStage) return '00:00';
    
    const startSec = timeToSeconds(startStage.time);
    const endSec = timeToSeconds(endStage.time);
    
    return secondsToTime(endSec - startSec);
  };

  const calculatePhaseRatio = (phaseStartKey: string, phaseEndKey: string) => {
    const startStage = stages.find(s => s.key === 'start');
    const endStage = stages.find(s => s.key === 'end');
    const phaseStartStage = stages.find(s => s.key === phaseStartKey);
    const phaseEndStage = stages.find(s => s.key === phaseEndKey);

    if (!startStage || !endStage || !phaseStartStage || !phaseEndStage) return '0.00';

    const totalSec = timeToSeconds(endStage.time) - timeToSeconds(startStage.time);
    const phaseSec = timeToSeconds(phaseEndStage.time) - timeToSeconds(phaseStartStage.time);

    if (totalSec <= 0 || phaseSec <= 0) return '0.00';

    return ((phaseSec / totalSec) * 100).toFixed(2);
  };


  // --- Derived Values ---
  const shrinkage = calculateShrinkage();
  const agtronWholeClass = getAgtronClassification(agtronWhole);
  const agtronWholeResult = { common: agtronWholeClass.common, scaa: agtronWholeClass.scaa };
  const agtronGroundClass = getAgtronClassification(agtronGround);
  const agtronGroundResult = { common: agtronGroundClass.common, scaa: agtronGroundClass.scaa };

  const dryingTime = calculateDuration('start', 'yellow');
  const maillardTime = calculateDuration('yellow', 'first_crack');
  const developmentTime = calculateDuration('first_crack', 'end');
  const dryingRatio = calculatePhaseRatio('start', 'yellow');
  const maillardRatio = calculatePhaseRatio('yellow', 'first_crack');
  const developmentRatio = calculatePhaseRatio('first_crack', 'end');

  return (
    <div className="space-y-8 mt-8">
      <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
        Roast Analysis
      </h3>

      <div className={compactLayout ? 'grid grid-cols-1 gap-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
        <RoastStageDataSection
          stages={stages}
          onStageChange={onStageChange}
          readOnly={readOnlyStages ?? readOnly}
          showTargetLabels={showTargetLabels}
        />
        <RoastTimePhasesSection
          showTargetLabels={showTargetLabels}
          dryingTime={dryingTime}
          dryingRatio={dryingRatio}
          maillardTime={maillardTime}
          maillardRatio={maillardRatio}
          developmentTime={developmentTime}
          developmentRatio={developmentRatio}
        />
      </div>

      <div className={compactLayout ? 'grid grid-cols-1 gap-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
        <RoastWeightAnalysisSection
          showTargetLabels={showTargetLabels}
          unit={unit}
          onToggle={onToggle}
          weightInput={weightInput}
          calculatedWeight={calculatedWeight}
          onWeightInputChange={onWeightInputChange}
          weightOutput={weightOutput}
          onWeightOutputChange={onWeightOutputChange}
          shrinkage={shrinkage}
          readOnly={readOnlyWeights ?? readOnly}
          disableWeightInput={disableWeightInput}
          showInputDiffWarning={showWorkOrderHints}
        />
        <RoastAgtronSection
          showTargetLabels={showTargetLabels}
          agtronWhole={agtronWhole}
          agtronGround={agtronGround}
          agtronWholeResult={agtronWholeResult}
          agtronGroundResult={agtronGroundResult}
          onAgtronChange={onAgtronChange}
          roastType={roastType}
          onRoastTypeChange={onRoastTypeChange}
          readOnly={readOnlyAgtron ?? readOnly}
          readOnlyRoastType={readOnlyRoastType}
          allowRoastTypeEdit={allowRoastTypeEdit}
        />
      </div>
    </div>
  );
};

export const RoastResultSection = React.memo(RoastResultSectionImpl);
